import MoreVertIcon from "@mui/icons-material/MoreVert";
import Drawer from "@mui/material/Drawer";
import Fab from "@mui/material/Fab";
import { type ReactNode, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { generatePath, useHistory, useParams } from "react-router-dom";
import routing from "routing";
import type { LessonFolder } from "src/components/Course/lessons/Lesson/RepetitionLesson";
import type { CourseSection } from "src/components/Course/lessons/Lessons";
import { Sections } from "src/components/Course/sections/sections";
import Menu from "src/components/Default/LekUI/Menu";
import { Tooltip } from "src/components/Default/LekUI/Tooltip";
import { useModals } from "src/components/Modals/context/ModalProvider";
import { ModalsNames } from "src/components/Modals/context/modals";
import { useContainerQueryContext } from "src/containers/Layouts/AppContentContext";
import {
	useCopyCourse,
	useEditCourse,
	useRemoveCourses,
} from "src/hooks/useCourses";
import ChartIcon from "src/images/Icons/newIcons/chart.svg";
import CopyExamIcon from "src/images/Icons/newIcons/copy.svg";
import DeleteIcon from "src/images/Icons/newIcons/delete.svg";
import ChangeNameIcon from "src/images/Icons/newIcons/edit.svg";
import HamburgerIcon from "src/images/Icons/newIcons/hamburger.svg";
import { MOBILE_BREAKPOINT } from "src/types/breakPoints";

import { SwipeableContent } from "../Default/LekUI/SwipableDrawer";

const DRAWER_BREAKPOINT = 700;

interface InfoMenuProps {
	children: ReactNode;
	isOpen: boolean;
	onClose: () => void;
}

const InfoMenu = ({ children, isOpen, onClose }: InfoMenuProps) => {
	const { width, windowWidth } = useContainerQueryContext();

	if (width > DRAWER_BREAKPOINT || windowWidth <= MOBILE_BREAKPOINT) {
		return children;
	}

	return (
		<Drawer
			anchor="right"
			className="course-info--drawer"
			open={isOpen}
			onClose={onClose}
		>
			{children}
		</Drawer>
	);
};

export interface CourseDetails {
	id: number;
	name: string;
	sections: CourseSection[];
	folders?: LessonFolder[];
	lastResolvedLesson?: { sectionId: number } | null;
}

interface CourseInfoProps {
	course: CourseDetails;
}

function CourseInfo({ course }: CourseInfoProps) {
	const { mutateAsync: copyCourse } = useCopyCourse() as unknown as {
		mutateAsync: (data: { courseId: number }) => Promise<{ id: number }>;
	};
	const { mutateAsync: removeCourses } = useRemoveCourses() as unknown as {
		mutateAsync: (ids: number[]) => Promise<void>;
	};
	const { width } = useContainerQueryContext();
	const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
	const { sectionId } = useParams<{ sectionId: string }>();
	const shouldRenderDrawer = width < DRAWER_BREAKPOINT;
	const { openModal } = useModals();
	const { t } = useTranslation();
	const history = useHistory();
	const { mutateAsync: renameCourse } = useEditCourse() as unknown as {
		mutateAsync: (payload: { id: number; name: string }) => Promise<unknown>;
	};
	const examsIds = course.sections
		.flatMap((section) => section.lessons)
		.map((lesson) => lesson.itemId)
		.filter(Boolean);

	useEffect(() => {
		setAnchorEl(null);
	}, []);

	const actionsItems = [
		{
			title: t("menuActions.course.edit"),
			onClick: () =>
				openModal(ModalsNames.ChangeNameModal, {
					title: t("modals.changeName.course.title", "Zmień nazwę kursu"),
					label: t("modals.changeName.course.label", "Nazwa kursu"),
					placeholder: t(
						"modals.changeName.course.placeholder",
						"Wpisz nazwę kursu",
					),
					name: course.name,
					onSave: (name: string) => renameCourse({ id: course.id, name }),
				}),
			Icon: <ChangeNameIcon className="icon-normal" />,
			testId: "course-menu-edit-item",
		},
		...(examsIds.length
			? [
					{
						title: t("menuActions.courseSection.statistics"),
						onClick: () => {
							openModal(ModalsNames.ExamsStatisticsModal, {
								examsIds,
							});
						},
						Icon: <ChartIcon className="icon-small" />,
					},
				]
			: []),
		{
			title: t("menuActions.course.copyCourse"),
			onClick: async () => {
				const { id } = await copyCourse({
					courseId: course.id,
				});

				const path = generatePath(routing.paths.course, { id });
				return history.push(path);
			},
			Icon: <CopyExamIcon className="icon-small" />,
			testId: "course-menu-copy-item",
		},
		{
			title: t("menuActions.course.remove"),
			className: "list-item-error",
			onClick: () => {
				openModal(ModalsNames.DeleteItemModal, {
					title: t("modals.deleteItem.course.title", "Usunąć kurs?"),
					description: t(
						"modals.deleteItem.course.description",
						"Kurs zostanie trwale usunięty razem z postępem nauki. Tej operacji nie można cofnąć.",
					),
					confirmText: t("modals.deleteItem.course.confirm", "Usuń kurs"),
					itemLabel: course.name,
					onDelete: async () => {
						await removeCourses([course.id]);
						history.push("/");
					},
				});
			},
			Icon: <DeleteIcon className="icon-small" />,
			testId: "course-menu-delete-item",
		},
	];

	const renderInfo = () => {
		return (
			<InfoMenu isOpen={!!anchorEl} onClose={() => setAnchorEl(null)}>
				<div className="basic-container course-info gap-12">
					<div className="flex gap-8 items-start flex-1 justify-between">
						<Tooltip title={course.name}>
							<p
								className="m-0 flex-1 size-18-light text-ellipsis-1"
								data-test-id="course-name"
							>
								{course.name}
							</p>
						</Tooltip>
						<Menu
							icon={<MoreVertIcon />}
							closeAfterClick
							items={actionsItems}
							buttonTestId="course-actions-menu-button"
						/>
					</div>
					<Sections
						sections={course.sections}
						courseId={course.id}
						activeSectionId={+sectionId}
					/>
				</div>
			</InfoMenu>
		);
	};

	const renderContent = () => {
		return (
			<SwipeableContent isOpen={!!anchorEl} onClose={() => setAnchorEl(null)}>
				{renderInfo()}
			</SwipeableContent>
		);
	};

	return (
		<div>
			{renderContent()}
			{shouldRenderDrawer && (
				<Fab
					className="course-info--fab fab"
					data-test-id="course-sections-fab"
					onClick={(e) => setAnchorEl(e.currentTarget)}
				>
					<span>{t("menuActions.course.sections")}</span>
					<HamburgerIcon className="icon-normal" />
				</Fab>
			)}
		</div>
	);
}

export default CourseInfo;
